import moment from 'moment';
import { booleanParaOpcao, opcaoParaBoolean } from 'utils/conversaoBooleanOption';
import {
  booleanOptions,
  BooleanValue,
  depositariaEmissaoOptions,
  naturezaPessoaOptions,
  negociacaoSecundariaOptions,
  tipoDistribuicaoPublicaOptions,
  tipoSerieOptions,
  unidadeFederativaOptions,
  tipoLiquidacaoOptions,
  contraparteRecebedoraOptions,
} from 'common/genericOptions';
import {
  CadastroCertificadoRecebiveisApiRequest,
  CertificadoRecebiveisApiResponse,
  CriteriosRemuneracao,
  DocumentInfo,
  NegociacaoSecundaria,
  TipoSerie,
  DocumentHistorico,
  TipoSerieHistorico,
  NegociacaoSecundariaHistorico,
  UploadDocumentos,
  TipoPagamento,
  type EventosFinanceiros,
} from 'common/genericTypes';
import {
  tipoSerieHistoricoDescriptions,
  negociacaoSecundariaHistoricoDescriptions,
} from 'common/genericDescriptions';
import { CaracteristicasState } from 'store/modules/certificadoRecebiveis/caracteristicas';
import { State as Emissor } from 'store/modules/certificadoRecebiveis/emissor';
import { addCpfCnpjMask } from 'utils/mask';
import { isValid as isDateValid } from 'utils/date';
import { periodicidadeStringParaNumero } from 'utils/periodicidadeMultaMoraConverter';
import { stripCpfCnpjFormatting } from 'utils/validation';

export default class CertificadoRecebiveisParser {
  static stateToCadastroCertificadoRecebiveisApiRequest(
    caracteristicas: CaracteristicasState,
    criteriosRemuneracao: CriteriosRemuneracao,
    eventos: EventosFinanceiros,
    documentos?: UploadDocumentos,
    emissor?: Emissor
  ): CadastroCertificadoRecebiveisApiRequest {
    const isTipoPagamentoSacOrPrice = criteriosRemuneracao.tipoPagamento && criteriosRemuneracao.tipoPagamento !== TipoPagamento.PagamentoFinal;

    let cr: CadastroCertificadoRecebiveisApiRequest = {
      draftId: caracteristicas?.draftId ?? undefined,
      emissorId: emissor?.id ?? undefined,
      codigoExterno: emissor?.codigoExterno ?? undefined,
      numeroEmissao: caracteristicas?.numeroEmissao,
      numeroSerie: caracteristicas?.numeroSerie,
      tipoSerie: caracteristicas?.tipoSerie?.value as TipoSerie,
      regimeFiduciario: caracteristicas?.regimeFiduciario?.value === BooleanValue?.Yes,
      distribuicaoPublica: caracteristicas?.distribuicaoPublica?.value === BooleanValue?.Yes,
      negociacaoSecundariaPor: caracteristicas?.negociacaoSecundariaPor?.value as NegociacaoSecundaria,
      termoSecuritizacaoFormalizado: caracteristicas?.termoSecuritizacaoFormalizado?.value === BooleanValue?.Yes,
      depositariaEmissao: caracteristicas?.depositariaEmissao?.value,
      codigoCvmCiaSecuritizadora: caracteristicas?.codigoCvmCiaSecuritizadora,
      utilizacaoAnuncioInicioDistribuicao:
        caracteristicas?.utilizacaoAnuncioInicioDistribuicao?.value === BooleanValue?.Yes,
      ufLocalEmissao: caracteristicas?.ufLocalEmissao?.value,
      localEmissao: caracteristicas?.localEmissao,
      classificadoraRisco1: caracteristicas?.classificadoraRisco1,
      rating1: caracteristicas?.rating1,
      classificadoraRisco2: caracteristicas?.classificadoraRisco2,
      rating2: caracteristicas?.rating2,
      ufLocalPagamento: caracteristicas?.ufLocalPagamento?.value,
      localPagamento: caracteristicas?.localPagamento,
      averbadoEmCartorio: caracteristicas?.averbadoEmCartorio?.value === BooleanValue?.Yes,
      registroNaInstituicaoCustodiante: caracteristicas?.registroNaInstituicaoCustodiante?.value === BooleanValue?.Yes,
      utilizacaoFaculdadeParagrafo3Art7: false,
      garantiaFlutuante: caracteristicas?.garantiaFlutuante?.value === BooleanValue?.Yes,
      tipoGarantia: caracteristicas?.tipoGarantia,
      ativoGarantia: caracteristicas?.ativoGarantia,
      valorGarantia: caracteristicas?.valorGarantia,
      nomeGarantidor: caracteristicas?.nomeGarantidor,
      cpfCnpjGarantidor: stripCpfCnpjFormatting(caracteristicas?.cpfCnpjGarantidor ?? ''),
      naturezaGarantidor: caracteristicas?.naturezaGarantidor?.value,
      nomeResponsavelPelasInformacoesDRI: caracteristicas?.nomeResponsavelPelasInformacoesDRI,
      emailResponsavelPelasInformacoesDRI: caracteristicas?.emailResponsavelPelasInformacoesDRI,
      bancoLiquidanteEmissor: caracteristicas?.bancoLiquidanteEmissor,
      cnpjBancoLiquidanteEmissor: stripCpfCnpjFormatting(caracteristicas?.cnpjBancoLiquidanteEmissor ?? ''),
      codigoBancoContaCorrenteVinculadaEmissao: caracteristicas?.codigoBancoContaCorrenteVinculadaEmissao,
      numeroAgenciaContaCorrenteVinculadaEmissao: caracteristicas?.numeroAgenciaContaCorrenteVinculadaEmissao,
      numeroContaCorrenteVinculadaEmissao: caracteristicas?.numeroContaCorrenteVinculadaEmissao,
      tipoLiquidacao: caracteristicas?.tipoLiquidacao?.value,
      descricaoAdicional: caracteristicas?.descricaoAdicional,
      chaveExterna: caracteristicas?.chaveExterna,
      isin: caracteristicas?.isin,
      coobrigacao: caracteristicas?.coobrigacao?.value === BooleanValue?.Yes,
      naturezaAgenteFiduciario: caracteristicas?.naturezaAgenteFiduciario?.value,
      razaoSocialAgenteFiduciario: caracteristicas?.razaoSocialAgenteFiduciario,
      cpfCnpjAgenteFiduciario: stripCpfCnpjFormatting(caracteristicas?.cpfCnpjAgenteFiduciario ?? ''),
      tipoDistribuicaoPublica: caracteristicas?.tipoDistribuicaoPublica?.value,
      contraparteRecebedora: caracteristicas?.contraparteRecebedora?.value,
      dataEncerramentoDistribuicao: caracteristicas?.dataEncerramentoDistribuicao
        ? moment(caracteristicas?.dataEncerramentoDistribuicao)?.format('YYYY-MM-DD HH:mm:ss')
        : undefined,
      coordenadoresIds: caracteristicas?.coordenadoresIds,
      coordenadorLiderId: caracteristicas?.coordenadorLiderId ?? undefined,
      razaoSocialEscriturador: caracteristicas?.razaoSocialEscriturador,
      cnpjEscriturador: stripCpfCnpjFormatting(caracteristicas?.cnpjEscriturador ?? ''),
      custodiantes: criteriosRemuneracao?.custodiantes ?? [],
      criteriosRemuneracao: {
        valorNominalUnitarioEmissao: criteriosRemuneracao?.valorNominalUnitarioEmissao,
        quantidadeEmitida: criteriosRemuneracao?.quantidadeEmitida,
        volumeEmissao: criteriosRemuneracao?.volumeEmissao,
        moedaEmissao: criteriosRemuneracao?.moedaEmissao,
        dataEmissao: criteriosRemuneracao?.dataEmissao
          ? moment(criteriosRemuneracao?.dataEmissao)?.format('YYYY-MM-DD HH:mm:ss')
          : undefined,
        dataVencimento: criteriosRemuneracao?.dataVencimento
          ? moment(criteriosRemuneracao?.dataVencimento)?.format('YYYY-MM-DD HH:mm:ss')
          : undefined,
        cotacaoInicial: criteriosRemuneracao?.cotacaoInicial,
        dataInicioRentabilidade: criteriosRemuneracao?.dataInicioRentabilidade
          ? moment(criteriosRemuneracao?.dataInicioRentabilidade)?.format('YYYY-MM-DD HH:mm:ss')
          : undefined,
        dataLimiteIntegralizacao: criteriosRemuneracao?.dataLimiteIntegralizacao
          ? moment(criteriosRemuneracao?.dataLimiteIntegralizacao)?.format('YYYY-MM-DD HH:mm:ss')
          : undefined,
        inicioRentabilidade: criteriosRemuneracao?.inicioRentabilidade!,
        indexador: criteriosRemuneracao?.remuneracao?.indexador,
        indexadorPorcentagem: criteriosRemuneracao?.remuneracao?.indexadorPorcentagem,
        defasagemFixing: criteriosRemuneracao?.remuneracao?.defasagemFixing,
        periodicidadeCorrecao: criteriosRemuneracao?.remuneracao?.periodicidadeCorrecao,
        proRata: criteriosRemuneracao?.remuneracao?.proRata,
        corrigePeriodoFinal: criteriosRemuneracao?.remuneracao?.corrigePeriodoFinal,
        diaAniversario: criteriosRemuneracao?.remuneracao?.diaAniversario,
        mesAniversario: criteriosRemuneracao?.remuneracao?.mesAniversario,
        tipoProjecao: criteriosRemuneracao?.remuneracao?.tipoProjecao,
        taxaJurosFixoSpread: criteriosRemuneracao?.remuneracao?.taxaJurosFixoSpreadPorcentagem,
        convencaoJurosFixos: criteriosRemuneracao?.remuneracao?.convencao,
        custoEmissao: {
          feeLaqusBruto: criteriosRemuneracao?.custoEmissao?.feeLaqusBruto,
          custoTotal: criteriosRemuneracao?.custoEmissao?.custoTotal ?? undefined,
          feeLaqusLiquido: criteriosRemuneracao?.custoEmissao?.feeLaqusLiquido ?? undefined,
          feeRepasse: criteriosRemuneracao?.custoEmissao?.feeRepasse ?? undefined
        },
        percentualMulta: criteriosRemuneracao?.percentualMulta ?? undefined,
        periodicidadeMulta: criteriosRemuneracao?.periodicidadeMulta ?? undefined,
        percentualMora: criteriosRemuneracao?.percentualMora ?? undefined,
        periodicidadeMora: criteriosRemuneracao?.periodicidadeMora ?? undefined,
      },
      amortizacaoSobre: eventos?.percentualAmortizacaoSobre,
      amortizacaoPassivelAoTermoSecuritizacao: eventos?.percentualAmortizacaoPassivelAlteracao,
      modalidadeAmortizacao: eventos?.modalidadeAmortizacao,
      amortizacoes: eventos?.amortizacoes,
      pagamentoJuros: {
        datas: eventos?.datasPagamentoJuros
          ? eventos?.datasPagamentoJuros?.filter((_) => !!_)?.map((d) => moment(d)?.format('YYYY-MM-DD HH:mm:ss'))
          : [],
      },
      incorporacaoJuros: {
        datas: eventos?.datasIncorporacaoJuros
          ? eventos?.datasIncorporacaoJuros?.filter((_) => !!_)?.map((d) => moment(d)?.format('YYYY-MM-DD HH:mm:ss'))
          : [],
      },
      possuiResgateAntecipado: eventos?.resgateAntecipadoFacultativo,
      vasosComunicantes: opcaoParaBoolean(caracteristicas?.possuiVasosComunicantes),
      tipoPagamento: criteriosRemuneracao.tipoPagamento ? criteriosRemuneracao.tipoPagamento : undefined,
      convencaoCalendarioPagamento: isTipoPagamentoSacOrPrice ? criteriosRemuneracao.convencaoCalendarioPagamento : undefined,
      primeiraParcelaAmortizacao: isTipoPagamentoSacOrPrice
        ? moment(criteriosRemuneracao.primeiraParcelaAmortizacao).format('YYYY-MM-DD HH:mm:ss')
        : undefined,
      primeiraParcelaJuros: isTipoPagamentoSacOrPrice
        ? moment(criteriosRemuneracao.primeiraParcelaJuros).format('YYYY-MM-DD HH:mm:ss')
        : undefined,
      periodicidadeAmortizacao: isTipoPagamentoSacOrPrice ? criteriosRemuneracao.periodicidadeAmortizacao : undefined,
      periodicidadeJuros: isTipoPagamentoSacOrPrice ? criteriosRemuneracao.periodicidadeJuros : undefined,
      documents:
        documentos?.documents
          ?.filter((doc) => !!doc?.remoteFileInfo)
          ?.map((doc) => ({
            id: doc?.remoteFileInfo!?.id,
            address: doc?.remoteFileInfo!?.address,
            emissionType: doc?.type,
            extension: doc?.remoteFileInfo!?.extension,
            fileName: doc?.remoteFileInfo!?.fileName,
            size: doc?.remoteFileInfo!?.size,
          })) ?? [],
      cnpjEmissor: caracteristicas?.cnpjEmissor ?? '',
    };

    return cr;
  }

  static apiResponseToCaracteristicasState(
    response: CertificadoRecebiveisApiResponse,
    copyId = true
  ): CaracteristicasState {
    const tipoSerie = tipoSerieOptions?.find((_) => _?.value === response?.tipoSerie);
    const regimeFiduciario = booleanOptions?.find(
      (_) => _?.value === (response?.regimeFiduciario ? BooleanValue?.Yes : BooleanValue?.No)
    );
    const coobrigacao = booleanOptions?.find(
      (_) => _?.value === (response?.coobrigacao ? BooleanValue?.Yes : BooleanValue?.No)
    );
    const distribuicaoPublica = booleanOptions?.find(
      (_) => _?.value === (response?.distribuicaoPublica ? BooleanValue?.Yes : BooleanValue?.No)
    );
    const tipoDistribuicaoPublica = tipoDistribuicaoPublicaOptions?.find(
      (_) => _?.value === response?.tipoDistribuicaoPublica
    );
    const contraparteRecebedora = contraparteRecebedoraOptions?.find(
      (_) => _?.value === response?.contraparteRecebedora
    );
    const negociacaoSecundariaPor = negociacaoSecundariaOptions?.find(
      (_) => _?.value === response?.negociacaoSecundariaPor
    );
    const termoSecuritizacaoFormalizado = booleanOptions?.find(
      (_) => _?.value === (response?.termoSecuritizacaoFormalizado ? BooleanValue?.Yes : BooleanValue?.No)
    );
    const depositariaEmissao = depositariaEmissaoOptions?.find((_) => _?.value === response?.depositariaEmissao);
    const utilizacaoAnuncioInicioDistribuicao = booleanOptions?.find(
      (_) => _?.value === (response?.utilizacaoAnuncioInicioDistribuicao ? BooleanValue?.Yes : BooleanValue?.No)
    );
    const ufLocalEmissao = unidadeFederativaOptions?.find((_) => _?.value === response?.ufLocalEmissao);
    const ufLocalPagamento = unidadeFederativaOptions?.find((_) => _?.value === response?.ufLocalPagamento);
    const averbadoEmCartorio = booleanOptions?.find(
      (_) => _?.value === (response?.averbadoEmCartorio ? BooleanValue?.Yes : BooleanValue?.No)
    );
    const registroNaInstituicaoCustodiante = booleanOptions?.find(
      (_) => _?.value === (response?.registroNaInstituicaoCustodiante ? BooleanValue?.Yes : BooleanValue?.No)
    );
    const utilizacaoFaculdadeParagrafo3Art7 = booleanOptions[1];
    const garantiaFlutuante = booleanOptions?.find(
      (_) => _?.value === (response?.garantiaFlutuante ? BooleanValue?.Yes : BooleanValue?.No)
    );
    const naturezaAgenteFiduciario = naturezaPessoaOptions?.find(
      (_) => _?.value === response?.naturezaAgenteFiduciario
    );
    const naturezaGarantidor = naturezaPessoaOptions?.find((_) => _?.value === response?.naturezaGarantidor);
    const tipoLiquidacao = tipoLiquidacaoOptions?.find((_) => _?.value === response?.tipoLiquidacao);

    let caracteristicasState: CaracteristicasState = {
      id: copyId ? response?.certificadoRecebiveisId : undefined,
      draftId: response?.draft?.id,
      isin: response?.isin,
      cnpjEmissor: response?.cnpjEmissor ?? '',
      distribuicaoPublica: distribuicaoPublica ?? booleanOptions[0],
      numeroEmissao: response?.numeroEmissao,
      numeroSerie: response?.numeroSerie,
      statusEmissao: response.statusEmissao,
      tipoSerie: tipoSerie ?? tipoSerieOptions[0],
      regimeFiduciario: regimeFiduciario ?? booleanOptions[0],
      coobrigacao: coobrigacao ?? booleanOptions[0],
      tipoDistribuicaoPublica: tipoDistribuicaoPublica ?? tipoDistribuicaoPublicaOptions[0],
      contraparteRecebedora: contraparteRecebedora ?? contraparteRecebedoraOptions[0],
      negociacaoSecundariaPor: negociacaoSecundariaPor ?? negociacaoSecundariaOptions[0],
      termoSecuritizacaoFormalizado: termoSecuritizacaoFormalizado ?? booleanOptions[0],
      depositariaEmissao: depositariaEmissao ?? depositariaEmissaoOptions[0],
      codigoCvmCiaSecuritizadora: response?.codigoCvmCiaSecuritizadora ?? '',
      utilizacaoAnuncioInicioDistribuicao: utilizacaoAnuncioInicioDistribuicao ?? booleanOptions[0],
      ufLocalEmissao: ufLocalEmissao ?? unidadeFederativaOptions[0],
      localEmissao: response?.localEmissao,
      dataEncerramentoDistribuicao: response?.dataEncerramentoDistribuicao,
      classificadoraRisco1: response?.classificadoraRisco1,
      rating1: response?.rating1,
      classificadoraRisco2: response?.classificadoraRisco2,
      rating2: response?.rating2,
      ufLocalPagamento: ufLocalPagamento ?? unidadeFederativaOptions[0],
      localPagamento: response?.localPagamento,
      averbadoEmCartorio: averbadoEmCartorio ?? booleanOptions[0],
      registroNaInstituicaoCustodiante: registroNaInstituicaoCustodiante ?? booleanOptions[0],
      utilizacaoFaculdadeParagrafo3Art7: utilizacaoFaculdadeParagrafo3Art7 ?? booleanOptions[1],
      garantiaFlutuante: garantiaFlutuante ?? booleanOptions[0],
      tipoGarantia: response?.tipoGarantia ?? '',
      ativoGarantia: response?.ativoGarantia ?? '',
      valorGarantia: response?.valorGarantia ?? 0,
      nomeResponsavelPelasInformacoesDRI: response?.nomeResponsavelPelasInformacoesDRI,
      emailResponsavelPelasInformacoesDRI: response?.emailResponsavelPelasInformacoesDRI,
      bancoLiquidanteEmissor: response?.bancoLiquidanteEmissor,
      cnpjBancoLiquidanteEmissor: response?.cnpjBancoLiquidanteEmissor,
      codigoBancoContaCorrenteVinculadaEmissao: response?.codigoBancoContaCorrenteVinculadaEmissao,
      numeroAgenciaContaCorrenteVinculadaEmissao: response?.numeroAgenciaContaCorrenteVinculadaEmissao,
      numeroContaCorrenteVinculadaEmissao: response?.numeroContaCorrenteVinculadaEmissao,
      tipoLiquidacao: tipoLiquidacao ?? tipoLiquidacaoOptions[0],
      chaveExterna: response?.chaveExterna,
      descricaoAdicional: response?.descricaoAdicional ?? '',
      razaoSocialEscriturador: response?.razaoSocialEscriturador ?? '',
      cnpjEscriturador: response?.cnpjEscriturador ?? '',
      coordenadoresIds: response?.coordenadores?.map((c) => c?.id) ?? [],
      coordenadorLiderId: response?.coordenadorLider?.id ?? '',
      razaoSocialAgenteFiduciario: response?.razaoSocialAgenteFiduciario ?? '',
      cpfCnpjAgenteFiduciario: response?.cpfCnpjAgenteFiduciario ?? '',
      naturezaAgenteFiduciario: naturezaAgenteFiduciario ?? naturezaPessoaOptions[0],
      nomeGarantidor: response?.nomeGarantidor ?? '',
      cpfCnpjGarantidor: response?.cpfCnpjGarantidor ?? '',
      naturezaGarantidor: naturezaGarantidor ?? naturezaPessoaOptions[0],
      possuiVasosComunicantes: booleanParaOpcao(response?.vasosComunicantes),
    };
    return caracteristicasState;
  }

  static apiResponseToCriteriosRemuneracaoState(response: CertificadoRecebiveisApiResponse): CriteriosRemuneracao {
    let criterios = response?.criteriosRemuneracao;

    const dataEmissao = moment(criterios?.dataEmissao);
    const dataVencimento = moment(criterios?.dataVencimento);
    const dataLimiteIntegralizacao = criterios?.dataLimiteIntegralizacao
    ? moment(criterios?.dataLimiteIntegralizacao)
    : undefined;
    const dataInicioRentabilidade = criterios?.dataInicioRentabilidade
      ? moment(criterios?.dataInicioRentabilidade)
      : undefined;
    

    let criteriosRemuneracaoState: CriteriosRemuneracao = {
      valorNominalUnitarioEmissao: criterios?.valorNominalUnitarioEmissao,
      quantidadeEmitida: criterios?.quantidadeEmitida,
      volumeEmissao: criterios?.volumeEmissao,
      moedaEmissao: criterios?.moedaEmissao,
      cotacaoInicial: criterios?.cotacaoInicial,
      dataEmissao: isDateValid(dataEmissao) ? dataEmissao : undefined,
      dataVencimento: isDateValid(dataVencimento) ? dataVencimento : undefined,
      convencaoJurosFixos: criterios?.convencaoJurosFixos,
      dataInicioRentabilidade: isDateValid(dataInicioRentabilidade) ? dataInicioRentabilidade : undefined,
      dataLimiteIntegralizacao: isDateValid(dataLimiteIntegralizacao) ? dataLimiteIntegralizacao : undefined,
      custodiantes: response.custodiantes ?? [],
      inicioRentabilidade: criterios?.inicioRentabilidade,
      remuneracao: {
        indexador: criterios?.indexador,
        defasagemFixing: criterios?.defasagemFixing,
        indexadorPorcentagem: criterios?.indexadorPorcentagem,
        periodicidadeCorrecao: criterios?.periodicidadeCorrecao,
        proRata: criterios?.proRata,
        corrigePeriodoFinal: criterios?.corrigePeriodoFinal,
        diaAniversario: criterios?.diaAniversario,
        mesAniversario: criterios?.mesAniversario,
        tipoProjecao: criterios?.tipoProjecao,
        taxaJurosFixoSpreadPorcentagem: criterios?.taxaJurosFixoSpread,
        convencao: criterios?.convencaoJurosFixos,
      },
      custoEmissao: {
        custoTotal: criterios?.custoEmissao?.custoTotal ?? undefined,
        feeLaqusBruto: criterios?.custoEmissao?.feeLaqusBruto,
        feeLaqusLiquido: criterios?.custoEmissao?.feeLaqusLiquido ?? undefined,
        feeRepasse: criterios.custoEmissao?.feeRepasse ?? undefined
      },
      tipoPagamento: response.integracaoMonitor?.tipoPagamento ?? '',
      convencaoCalendarioPagamento: response.integracaoMonitor?.convencaoCalendarioPagamento ?? '',
      periodicidadeAmortizacao: response.integracaoMonitor?.periodicidadeAmortizacao ?? '',
      primeiraParcelaAmortizacao: response.integracaoMonitor?.primeiraParcelaAmortizacao ?? null,
      periodicidadeJuros: response.integracaoMonitor?.periodicidadeJuros ?? '',
      primeiraParcelaJuros: response.integracaoMonitor?.primeiraParcelaJuros ?? null,
      percentualMulta: response.criteriosRemuneracao?.percentualMulta ?? undefined,
      periodicidadeMulta: periodicidadeStringParaNumero(response.criteriosRemuneracao?.periodicidadeMulta as unknown as string),
      percentualMora: response.criteriosRemuneracao?.percentualMora ?? undefined,
      periodicidadeMora: periodicidadeStringParaNumero(response.criteriosRemuneracao?.periodicidadeMora as unknown as string),
    };
    return criteriosRemuneracaoState;
  }

  static apiResponseToEventosState(response: CertificadoRecebiveisApiResponse): EventosFinanceiros {
    let eventosState: EventosFinanceiros = {
      datasPagamentoJuros: response?.pagamentoJuros?.datas?.map((_) => moment(_)),
      percentualAmortizacaoPassivelAlteracao: response?.amortizacaoPassivelAoTermoSecuritizacao,
      percentualAmortizacaoSobre: response?.amortizacaoSobre,
      amortizacoes: response?.amortizacoes,
      datasIncorporacaoJuros: response?.incorporacaoJuros?.datas?.map((_) => moment(_)),
      resgateAntecipadoFacultativo: response?.possuiResgateAntecipado,
      modalidadeAmortizacao: response?.modalidadeAmortizacao,
    };
    return eventosState;
  }

  static apiResponseToCertificadoRecebiveisDocumentsState(
    response: CertificadoRecebiveisApiResponse
  ): Array<DocumentInfo> {
    let documents: Array<DocumentInfo> = [];
    if (response?.documents && response?.documents?.length > 0) {
      documents = response?.documents?.map((doc) => ({
        type: doc?.emissionType,
        remoteFileInfo: doc,
      }));
    }
    return documents;
  }

  static formatarCampoAlteracoes(nomeDoCampo: string, campo: any) {
    switch (nomeDoCampo) {
      case 'TermoSecuritizacaoFormalizado':
      case 'RegimeFiduciario':
      case 'UtilizacaoFaculdadeParagrafo3Art7':
      case 'GarantiaFlutuante':
      case 'DistribuicaoPublica':
      case 'AverbadoEmCartorio':
      case 'RegistroNaInstituicaoCustodiante':
        return JSON?.parse(campo) === true ? 'Sim' : 'Não';
      case 'Coobrigacao':
      case 'UtilizacaoAnuncioInicioDistribuicao':
        const uParsed = JSON?.parse(campo);
        if (uParsed === null) return '';
        return uParsed === true ? 'Sim' : 'Não';
      case 'DataEncerramentoDistribuicao':
        const dedParsed = JSON?.parse(campo);
        if (!dedParsed) return '';
        return moment(dedParsed)?.format('DD/MM/YYYY');
      case 'TipoSerie':
        const tsParsed = JSON?.parse(campo);
        if (isNaN(tsParsed)) return '';
        return tipoSerieHistoricoDescriptions[tsParsed as TipoSerieHistorico];
      case 'NegociacaoSecundariaPor':
        return negociacaoSecundariaHistoricoDescriptions[JSON?.parse(campo) as NegociacaoSecundariaHistorico];
      case 'CnpjInstituicaoCustodiante':
      case 'CpfCnpjGarantidor':
      case 'CnpjBancoLiquidanteEmissor':
      case 'CpfCnpjAgenteFiduciario':
      case 'CnpjCoordenadorLiderDistribuidor':
      case 'CnpjEscriturador':
        return addCpfCnpjMask(JSON?.parse(campo));
      case 'Documents':
        const parsed = JSON?.parse(campo);
        if (!parsed || parsed?.length === 0) return [];
        const documentos = parsed as Array<DocumentHistorico>;
        return documentos;
      default:
        return JSON?.parse(campo) as string;
    }
  }
}
